import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  FaServer,
  FaLock,
  FaShieldAlt,
  FaSyncAlt,
  FaUserShield,
  FaCloud,
  FaNetworkWired,
  FaKey,
  FaUserLock,
  FaClipboardList,
  FaCheckCircle,
  FaGavel,
  FaGlobeAsia,
  FaDatabase,
  FaBell,
  FaLifeRing,
  FaBan,
  FaUsers,
  FaFileContract,
  FaSearch,
  FaQuestionCircle,
} from 'react-icons/fa';
import { DATA_PRIVACY } from '../config/content';
import styles from './ResponsibleAI.module.css';
import SEO from '../components/SEO';

const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const TAB_ICONS: Record<string, React.ReactNode> = {
  infrastructure: <FaServer />,
  encryption: <FaLock />,
  compliance: <FaShieldAlt />,
  continuity: <FaSyncAlt />,
  'ai-confidentiality': <FaUserShield />,
};

const TAB_CATEGORIES: Record<string, string> = {
  infrastructure: 'Hosting & Network',
  encryption: 'Cryptography & Access',
  compliance: 'Privacy Law & Certifications',
  continuity: 'Resilience & Response',
  'ai-confidentiality': 'AI Confidentiality',
};

const ITEM_ICONS: Record<string, React.ReactNode[]> = {
  infrastructure: [<FaGlobeAsia />, <FaCloud />, <FaShieldAlt />, <FaNetworkWired />],
  encryption: [<FaDatabase />, <FaKey />, <FaUserLock />, <FaClipboardList />],
  compliance: [<FaGavel />, <FaCheckCircle />, <FaShieldAlt />, <FaGlobeAsia />],
  continuity: [<FaDatabase />, <FaSyncAlt />, <FaBell />, <FaLifeRing />],
  'ai-confidentiality': [<FaBan />, <FaUsers />, <FaFileContract />, <FaSearch />],
};

const PILLAR_BODY_STYLE: React.CSSProperties = {
  fontSize: 13.5,
  color: '#8b95a8',
  lineHeight: 1.75,
  fontWeight: 300,
  margin: 0,
};

const DataPrivacy: React.FC = () => {
  const [activeTab, setActiveTab] = useState(DATA_PRIVACY.tabs[0].key);
  const activeData = DATA_PRIVACY.tabs.find((t) => t.key === activeTab)!;
  const icons = ITEM_ICONS[activeTab] || [];

  return (
    <main className={styles.page}>
      <SEO path="/data-privacy" />
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <div className={styles.hero}>
          <motion.div
            className={styles.heroInner}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className={styles.heroContent}>
              <div className={styles.heroText}>
                <div className={styles.heroBadge}>
                  <div className={styles.badgeDot} />
                  <span>{DATA_PRIVACY.hero.label}</span>
                </div>
                <h1>
                  Your Data, <em>Protected by Design</em>
                </h1>
                <p className={styles.heroSub}>{DATA_PRIVACY.hero.subtitle}</p>
              </div>
              <div className={styles.heroImageWrap}>
                <img
                  src="/images/data-privacy/hero-banner.svg"
                  alt="Data Privacy"
                  className={styles.heroImage}
                />
              </div>
            </div>
          </motion.div>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* ── Sidebar tabs + content (mirrors Responsible AI / Governance layout) ── */}
      <section className={styles.mainLayout}>
        <aside className={styles.sidebar}>
          <nav className={styles.sidebarNav}>
            {DATA_PRIVACY.tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  className={`${styles.sidebarItem} ${isActive ? styles.sidebarItemActive : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span className={styles.sidebarIcon}>{TAB_ICONS[tab.key]}</span>
                  <span className={styles.sidebarText}>
                    <span className={styles.sidebarLabel}>{tab.label}</span>
                    <span className={styles.sidebarDesc}>{DATA_PRIVACY.tabDescriptions[tab.key]}</span>
                  </span>
                  {isActive && <div className={styles.sidebarActiveIndicator} />}
                </button>
              );
            })}
          </nav>
        </aside>

        <div className={styles.content}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Tab header */}
              <div className={styles.sectionTag}>{activeData.label}</div>
              <h2 className={styles.contentTitle}>{activeData.title}</h2>
              <p className={styles.contentSub}>{activeData.subtitle}</p>

              {/* Intro */}
              <p className={styles.introText}>{activeData.intro}</p>

              {/* Overview pillars — appears only on first tab as a “five pillars” style summary */}
              {activeTab === DATA_PRIVACY.tabs[0].key && (
                <Reveal>
                  <h3 className={styles.governanceSectionTitle}>Security at a Glance</h3>
                  <div className={styles.frameworkDefGrid}>
                    {DATA_PRIVACY.summary.pillars.map((p) => (
                      <div className={styles.frameworkDefItem} key={p.label}>
                        <div className={styles.frameworkDefIcon}>
                          <FaCheckCircle />
                        </div>
                        <div className={styles.frameworkDefContent}>
                          <span className={styles.frameworkDefLabel}>{p.label}</span>
                          <p>{p.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              )}

              {/* Pillar-style key safeguards (matches the Five Pillars pattern) */}
              <Reveal>
                <h3 className={styles.governanceSectionTitle}>Key Safeguards</h3>
                <div className={styles.pillarsContainer}>
                  {activeData.items.map((item, i) => (
                    <div className={styles.pillarSection} key={item.title}>
                      <div className={styles.pillarHeader}>
                        <div className={styles.pillarIcon}>
                          {icons[i] || <FaCheckCircle />}
                        </div>
                        <div>
                          <h4 className={styles.pillarTitle}>{item.title}</h4>
                          <span className={styles.pillarCategory}>
                            {TAB_CATEGORIES[activeTab]}
                          </span>
                        </div>
                      </div>
                      <p style={PILLAR_BODY_STYLE}>{item.description}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── FAQ ── */}
      <section className={styles.mainLayout} style={{ alignItems: 'stretch' }}>
        <div className={styles.content} style={{ width: '100%' }}>
          <div className={styles.sectionTag}>{DATA_PRIVACY.faq.label}</div>
          <h2 className={styles.contentTitle}>{DATA_PRIVACY.faq.title}</h2>

          <Reveal>
            <div className={styles.pillarsContainer}>
              {DATA_PRIVACY.faq.items.map((item) => (
                <div className={styles.pillarSection} key={item.question}>
                  <div className={styles.pillarHeader}>
                    <div className={styles.pillarIcon}>
                      <FaQuestionCircle />
                    </div>
                    <div>
                      <h4 className={styles.pillarTitle}>{item.question}</h4>
                      <span className={styles.pillarCategory}>FAQ</span>
                    </div>
                  </div>
                  <p style={PILLAR_BODY_STYLE}>{item.answer}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── CTA ── */}
      <div className={styles.ctaSection}>
        <Reveal className={styles.ctaBanner}>
          <div>
            <h2>
              Confidence Begins with<br />
              <em>Clarity</em>
            </h2>
            <p>{DATA_PRIVACY.cta.subtitle}</p>
          </div>
          <div className={styles.ctaActions}>
            <Link
              to={DATA_PRIVACY.cta.ctaPrimaryLink}
              className={styles.btnLarge}
              style={{ textAlign: 'center' }}
            >
              {DATA_PRIVACY.cta.ctaPrimary}
            </Link>
            <Link
              to={DATA_PRIVACY.cta.ctaSecondaryLink}
              className={styles.btnGhost}
              style={{ textAlign: 'center' }}
            >
              {DATA_PRIVACY.cta.ctaSecondary}
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default DataPrivacy;
